/**
 * Renders a document's summary (a small Markdown text from a .txt file that
 * anyone with access to the folder may have written) into safe DOM nodes.
 *
 * marked turns it into HTML, DOMPurify then keeps only a fixed set of
 * formatting tags: no images, no scripts, no raw HTML, no attributes except
 * href, and only http(s)/mailto links (which always open in a new tab without
 * giving the target page access to this one).
 */
import DOMPurify from "dompurify";
import { marked } from "marked";

const ALLOWED_TAGS = [
  "p", "br", "hr", "strong", "em", "del", "code", "pre", "blockquote",
  "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "a",
];

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.nodeName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

export function renderMarkdown(source: string): DocumentFragment {
  // Single newlines become line breaks: summaries are short notes, not prose.
  const html = marked.parse(source, { async: false, breaks: true, gfm: true });
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ["href"],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:)/i,
    RETURN_DOM_FRAGMENT: true,
  });
}
